import { lazy } from 'react';

import type {
  IntegrationDefinition,
  IntegrationLoaderContext,
  NormalizedConnection,
} from '@mint-lib/integration-registry';
import type { QueryClient } from '@mint-lib/query-client';
import {
  GoogleAnalyticsIntegrations,
  PlatformUrns,
} from '@mint-sdk/api-integrations';

import { createGA4OAuthFlow } from './oauth.js';
import {
  createGA4Repository,
  toGA4NormalizedConnection,
} from './repository.js';

const GA4_URN = PlatformUrns.TRAFFICKING_PLATFORM_URN.googleAnalytics;

/**
 * Loader-safe prefetch: repository methods are hooks and can't be called
 * outside React, so this calls the same `getGA4Integrations` query directly
 * against the loader's `queryClient` and reuses the same list-then-find-shim
 * mapping `repository.ts`'s `useList` uses, so `apps/routing`'s loaders can
 * redirect read-only users to their first existing connection.
 */
async function prefetch(
  ctx: IntegrationLoaderContext,
): Promise<{ connections?: NormalizedConnection[] } | void> {
  const queryClient = ctx.queryClient as QueryClient;
  const items = await queryClient.ensureQueryData(
    GoogleAnalyticsIntegrations.getGA4Integrations({}),
  );
  // Only the ids are used (read-only redirect), so the flag-dependent
  // `isExpired` mapping doesn't matter here.
  return {
    connections: (items ?? []).map((item) => toGA4NormalizedConnection(item)),
  };
}

export function createGA4Integration(): IntegrationDefinition {
  return {
    meta: {
      urn: GA4_URN,
      displayName: 'Google Analytics 4',
      i18nNamespace: '@mint-lib/ga4-integration',
      channel: 'analytics',
      direction: 'one-way',
      requiresGooglePolicyNotice: true,
    },
    connection: {
      // Never imported eagerly — always via `React.lazy`, so `src/index.ts`
      // (which re-exports this factory) stays free of the form chunks.
      AddForm: lazy(() => import('@mint-lib/ga4-integration/add-form')),
      ManageForm: lazy(() => import('@mint-lib/ga4-integration/manage-form')),
      repository: createGA4Repository(),
      oauth: createGA4OAuthFlow(),
      prefetch,
    },
  };
}
